import { useState, useEffect, useCallback } from "react";
import { api, formatCurrency, getErrorMessage } from "@/lib/api";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Loader2, Trash2, Clock, TrendingUp, TrendingDown, Minus,
  FileDown, FileSpreadsheet, Users,
} from "lucide-react";

import {
  TYPE_COLORS, TYPE_LABELS, TYPE_COLORS_LIGHT, DAY_NAMES,
  getWeekDates, getMonthDates, dateStr, getDefaultSlots,
} from "@/lib/workTrackingHelpers";
import AssignWorkDialog from "@/components/work/AssignWorkDialog";
import EditWorkEntryDialog from "@/components/work/EditWorkEntryDialog";
import WorkPeopleDialog from "@/components/work/WorkPeopleDialog";

const WorkTrackingTab = () => {
  const [viewMode, setViewMode] = useState("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [orders, setOrders] = useState([]);
  const [savs, setSavs] = useState([]);
  const [entries, setEntries] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subcontractors, setSubcontractors] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [calendarColors, setCalendarColors] = useState({});

  // Visibility prefs (per-user)
  const [hiddenWorkerIds, setHiddenWorkerIds] = useState({});
  const [showInactive, setShowInactive] = useState(false);
  const [peopleDialog, setPeopleDialog] = useState(false);

  // Assign dialog
  const [assignDialog, setAssignDialog] = useState(false);
  const [selection, setSelection] = useState(null);

  // Edit dialog
  const [editEntry, setEditEntry] = useState(null);

  // Export
  const [exportEmployeeId, setExportEmployeeId] = useState("all");

  const dates = viewMode === "week"
    ? getWeekDates(currentDate)
    : getMonthDates(currentDate.getFullYear(), currentDate.getMonth());

  const startStr = dates.length > 0 ? dateStr(dates[0]) : "";
  const endStr = dates.length > 0 ? dateStr(dates[dates.length - 1]) : "";

  const fetchData = useCallback(async () => {
    if (!startStr || !endStr) return;
    try {
      setLoading(true);
      const [empRes, subRes, ordRes, savRes, entRes, compRes, supRes, colorsRes] = await Promise.all([
        api.getEmployees(),
        api.getSubcontractors?.().catch(() => ({ data: [] })) ?? Promise.resolve({ data: [] }),
        api.getOrders({ status: "pending" }),
        api.getSAVs(),
        api.getWorkEntries({ start_date: startStr, end_date: endStr }),
        api.getWorkComparison({ start_date: startStr, end_date: endStr }),
        api.getSuppliers?.() ?? Promise.resolve({ data: [] }),
        api.getCalendarColors?.().catch(() => ({ data: { colors: {} } })) ?? Promise.resolve({ data: { colors: {} } }),
      ]);
      const allEmps = empRes.data || [];
      setAllEmployees(allEmps);
      setEmployees(allEmps.filter(e => !e.exit_date));
      setSubcontractors(subRes.data || []);
      setOrders(ordRes.data);
      setSavs(savRes.data);
      setEntries(entRes.data);
      setComparison(compRes.data);
      setSuppliers(supRes.data || []);
      setCalendarColors(colorsRes.data?.colors || {});
    } catch (_e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [startStr, endStr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load per-user visibility prefs
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.getUserTablePref?.("work_tracking_visible_people") || {};
        if (data?.hidden) setHiddenWorkerIds(data.hidden);
        if (typeof data?.show_inactive === "boolean") setShowInactive(data.show_inactive);
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  const persistPeoplePrefs = async (hidden, inactiveFlag) => {
    try {
      await api.upsertUserTablePref?.("work_tracking_visible_people", {
        hidden, show_inactive: inactiveFlag,
      });
    } catch (_e) { /* ignore */ }
  };

  const toggleWorkerVisibility = (kind, id) => {
    const key = `${kind}:${id}`;
    const next = { ...hiddenWorkerIds, [key]: !hiddenWorkerIds[key] };
    if (!next[key]) delete next[key];
    setHiddenWorkerIds(next);
    persistPeoplePrefs(next, showInactive);
  };

  const toggleShowInactive = () => {
    const next = !showInactive;
    setShowInactive(next);
    persistPeoplePrefs(hiddenWorkerIds, next);
  };

  const isWorkerVisible = (kind, id) => !hiddenWorkerIds[`${kind}:${id}`];

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (viewMode === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const getEntriesFor = (kind, id, dt) => {
    const ds = dateStr(dt);
    if (kind === "subcontractor") {
      return entries.filter(e => e.subcontractor_id === id && e.date === ds);
    }
    return entries.filter(e => e.employee_id === id && !e.subcontractor_id && e.date === ds);
  };
  const getMorningEntries = (kind, id, dt) => getEntriesFor(kind, id, dt).filter(e => e.start_time < "12:30");
  const getAfternoonEntries = (kind, id, dt) => getEntriesFor(kind, id, dt).filter(e => e.start_time >= "12:30");

  const openAssign = (workerKind, workerId, dt, slot) => {
    setSelection({ workerKind, workerId, dates: [dateStr(dt)], slot });
    setAssignDialog(true);
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await api.deleteWorkEntry(entryId);
      toast.success("Entrée supprimée");
      fetchData();
    } catch (_e) { toast.error("Erreur"); }
  };

  const handleExportPDF = async () => {
    try {
      const { default: jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");

      const period = viewMode === "week"
        ? `Semaine du ${dates[0]?.toLocaleDateString("fr-FR")} au ${dates[dates.length - 1]?.toLocaleDateString("fr-FR")}`
        : currentDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

      const targets = exportEmployeeId === "all"
        ? employees
        : employees.filter(e => e.id === exportEmployeeId);

      if (targets.length === 0) { toast.error("Aucun salarié sélectionné"); return; }

      toast.info("Génération du PDF...");
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

      targets.forEach((emp, idx) => {
        if (idx > 0) pdf.addPage();
        pdf.setFontSize(16);
        pdf.setFont("helvetica", "bold");
        pdf.text(`Suivi des chantiers - ${emp.first_name} ${emp.last_name}`, 14, 15);
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.text(period, 14, 21);
        pdf.text(`Exporté le ${new Date().toLocaleDateString("fr-FR")}`, 14, 26);

        const rows = dates.map(dt => {
          const m = getMorningEntries("employee", emp.id, dt)
            .map(e => `${e.reference_name || e.custom_label || TYPE_LABELS[e.entry_type]} (${e.start_time}-${e.end_time})`)
            .join("\n") || "-";
          const a = getAfternoonEntries("employee", emp.id, dt)
            .map(e => `${e.reference_name || e.custom_label || TYPE_LABELS[e.entry_type]} (${e.start_time}-${e.end_time})`)
            .join("\n") || "-";
          const dayEntries = getEntriesFor("employee", emp.id, dt);
          const totalH = dayEntries.reduce((s, e) => s + (e.hours || 0), 0);
          return [
            `${DAY_NAMES[dt.getDay()]} ${dt.getDate()}/${dt.getMonth() + 1}`,
            m, a,
            totalH > 0 ? `${totalH.toFixed(2)}h` : "-",
          ];
        });

        const totalWeek = dates.reduce((s, dt) => s + getEntriesFor("employee", emp.id, dt).reduce((ss, e) => ss + (e.hours || 0), 0), 0);
        rows.push(["TOTAL", "", "", `${totalWeek.toFixed(2)}h`]);

        autoTable(pdf, {
          startY: 32,
          head: [["Jour", "Matin", "Après-midi", "Total"]],
          body: rows,
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: { fillColor: [234, 88, 12] },
          columnStyles: {
            0: { cellWidth: 35, fontStyle: "bold" },
            1: { cellWidth: 110 },
            2: { cellWidth: 110 },
            3: { cellWidth: 25, halign: "right", fontStyle: "bold" },
          },
          didParseCell: (d) => {
            if (d.row.index === rows.length - 1) {
              d.cell.styles.fillColor = [245, 245, 245];
              d.cell.styles.fontStyle = "bold";
            }
          },
        });

        const sigY = pdf.internal.pageSize.getHeight() - 35;
        pdf.setDrawColor(120);
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        pdf.text("Signature du salarié :", 20, sigY);
        pdf.line(20, sigY + 20, 120, sigY + 20);
        pdf.text("Signature du responsable :", 170, sigY);
        pdf.line(170, sigY + 20, 270, sigY + 20);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.text("Date : ____________________", 20, sigY + 26);
        pdf.text("Date : ____________________", 170, sigY + 26);
      });

      const fname = exportEmployeeId === "all"
        ? `suivi-chantiers_tous_${viewMode}_${startStr}`
        : `suivi-chantiers_${targets[0].last_name}_${viewMode}_${startStr}`;
      pdf.save(`${fname}.pdf`);
      toast.success("PDF exporté");
    } catch (e) {
      console.error(e);
      toast.error("Erreur export PDF");
    }
  };

  const buildEmployeeSheet = (emp) => {
    const header = ["Jour", "Matin", "Après-midi", "Total (h)"];
    const rows = [header];
    let totalWeek = 0;
    dates.forEach(dt => {
      const m = getMorningEntries("employee", emp.id, dt).map(e => `${e.reference_name || e.custom_label || TYPE_LABELS[e.entry_type]} (${e.start_time}-${e.end_time})`).join(" | ");
      const a = getAfternoonEntries("employee", emp.id, dt).map(e => `${e.reference_name || e.custom_label || TYPE_LABELS[e.entry_type]} (${e.start_time}-${e.end_time})`).join(" | ");
      const dayH = getEntriesFor("employee", emp.id, dt).reduce((s, e) => s + (e.hours || 0), 0);
      totalWeek += dayH;
      rows.push([`${DAY_NAMES[dt.getDay()]} ${dt.getDate()}/${dt.getMonth() + 1}`, m, a, dayH]);
    });
    rows.push(["TOTAL", "", "", totalWeek]);
    rows.push([]);
    rows.push(["Signature salarié :", "", "Signature responsable :", ""]);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 14 }, { wch: 40 }, { wch: 40 }, { wch: 10 }];
    return ws;
  };

  const handleExportExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      if (exportEmployeeId === "all") {
        const header = ["Salarié"];
        dates.forEach(dt => {
          const label = `${DAY_NAMES[dt.getDay()]} ${dt.getDate()}/${dt.getMonth() + 1}`;
          header.push(`${label} - Matin`);
          header.push(`${label} - Après-midi`);
        });
        const overview = [header];
        employees.forEach(emp => {
          const row = [`${emp.first_name} ${emp.last_name}`];
          dates.forEach(dt => {
            const m = getMorningEntries("employee", emp.id, dt).map(e => `${e.reference_name || e.custom_label || TYPE_LABELS[e.entry_type]} (${e.start_time}-${e.end_time})`).join(" | ");
            const a = getAfternoonEntries("employee", emp.id, dt).map(e => `${e.reference_name || e.custom_label || TYPE_LABELS[e.entry_type]} (${e.start_time}-${e.end_time})`).join(" | ");
            row.push(m); row.push(a);
          });
          overview.push(row);
        });
        const wsOverview = XLSX.utils.aoa_to_sheet(overview);
        wsOverview["!cols"] = header.map((_, i) => ({ wch: i === 0 ? 22 : 26 }));
        XLSX.utils.book_append_sheet(wb, wsOverview, "Récapitulatif");

        employees.forEach(emp => {
          const ws = buildEmployeeSheet(emp);
          const safeName = `${emp.first_name} ${emp.last_name}`.slice(0, 28).replace(/[\\/?*[\]:]/g, "");
          XLSX.utils.book_append_sheet(wb, ws, safeName || emp.id.slice(0, 6));
        });

        if (comparison) {
          const compHeader = ["Type", "Référence", "Client", "Jours théoriques", "Heures réelles", "Jours réels", "Écart (jours)", "Écart (%)", "Statut"];
          const compRows = [compHeader];
          (comparison.orders || []).forEach(o => compRows.push(["Commande", o.reference, o.client_name, o.theoretical_days, o.actual_hours, o.actual_days, o.delta_days, o.delta_percent, o.status]));
          (comparison.savs || []).forEach(s => compRows.push(["SAV", "-", s.client_name, s.theoretical_days, s.actual_hours, s.actual_days, s.delta_days, "", s.status]));
          const wsc = XLSX.utils.aoa_to_sheet(compRows);
          wsc["!cols"] = compHeader.map(() => ({ wch: 16 }));
          XLSX.utils.book_append_sheet(wb, wsc, "Comparaison");
        }

        XLSX.writeFile(wb, `suivi-chantiers_tous_${viewMode}_${startStr}.xlsx`);
      } else {
        const emp = employees.find(e => e.id === exportEmployeeId);
        if (!emp) { toast.error("Salarié introuvable"); return; }
        const ws = buildEmployeeSheet(emp);
        XLSX.utils.book_append_sheet(wb, ws, "Pointage");
        XLSX.writeFile(wb, `suivi-chantiers_${emp.last_name}_${viewMode}_${startStr}.xlsx`);
      }

      toast.success("Export Excel généré");
    } catch (e) {
      console.error(e);
      toast.error("Erreur export Excel");
    }
  };

  const getEntryColor = (e) => {
    if (e.entry_type === "commande" && e.order_type_color) {
      const tc = e.order_type_text_color === "black" ? "#0f172a"
        : e.order_type_text_color === "gray" ? "#475569"
        : "#ffffff";
      return { bg: e.order_type_color, fg: tc };
    }
    const key = e.entry_type === "sav" ? "sav"
      : e.entry_type === "atelier" ? "atelier"
      : e.entry_type === "fournisseur" ? "fournisseur"
      : null;
    return key && calendarColors[key] ? { bg: calendarColors[key], fg: "#ffffff" } : null;
  };

  const getEntryLabel = (e) => {
    if (e.entry_type === "commande") return e.client_name || e.reference_name || "Commande";
    if (e.entry_type === "sav") return e.client_name ? `SAV ${e.client_name}` : (e.reference_name || "SAV");
    return e.custom_label || TYPE_LABELS[e.entry_type] || "Autre";
  };

  const getEntryTooltip = (e) => {
    if (e.entry_type === "commande") {
      const parts = [];
      if (e.client_name) parts.push(e.client_name);
      if (e.city) parts.push(e.city);
      if (e.order_type_name) parts.push(e.order_type_name);
      const head = parts.length ? parts.join(" — ") : (e.reference_name || "Commande");
      const ref = e.reference_name ? `\nRéf : ${e.reference_name}` : "";
      return `${head}${ref}\nHoraires : ${e.start_time}-${e.end_time}${e.comment ? `\n💬 ${e.comment}` : ""}`;
    }
    if (e.entry_type === "sav") {
      const head = e.client_name ? `SAV — ${e.client_name}` : (e.reference_name || "SAV");
      const desc = e.sav_description ? `\n${e.sav_description}` : "";
      return `${head}${desc}\nHoraires : ${e.start_time}-${e.end_time}${e.comment ? `\n💬 ${e.comment}` : ""}`;
    }
    const label = e.custom_label || TYPE_LABELS[e.entry_type] || "Autre";
    return `${label}\nHoraires : ${e.start_time}-${e.end_time}${e.comment ? `\n💬 ${e.comment}` : ""}`;
  };

  const renderCell = (kind, id, dt, slot) => {
    const cellEntries = slot === "morning" ? getMorningEntries(kind, id, dt) : getAfternoonEntries(kind, id, dt);
    if (cellEntries.length === 0) {
      return (
        <div
          className="h-full min-h-[28px] cursor-pointer hover:bg-zinc-100 rounded-sm transition-all duration-200 ease-out flex items-center justify-center"
          onClick={() => openAssign(kind, id, dt, slot)}
          data-testid={`cell-${kind}-${id}-${dateStr(dt)}-${slot}`}
        >
          <span className="text-muted-foreground/30 text-xs">+</span>
        </div>
      );
    }
    return (
      <div className="space-y-0.5">
        {cellEntries.map(e => {
          const customColor = getEntryColor(e);
          const label = getEntryLabel(e);
          const tooltip = getEntryTooltip(e);
          return (
            <div
              key={e.id}
              className={`text-[10px] px-1 py-0.5 rounded border truncate flex items-center gap-0.5 group ${customColor ? "" : (TYPE_COLORS_LIGHT[e.entry_type] || TYPE_COLORS_LIGHT.custom)}`}
              style={customColor ? { backgroundColor: customColor.bg, color: customColor.fg, borderColor: "transparent" } : {}}
              title={tooltip}
              data-testid={`entry-${e.id}`}
            >
              <span className="truncate flex-1">{label}</span>
              {e.comment && (
                <span className="opacity-70 shrink-0" title={e.comment}>💬</span>
              )}
              <button
                onClick={(ev) => { ev.stopPropagation(); setEditEntry(e); }}
                className="opacity-0 group-hover:opacity-100 shrink-0 transition-opacity"
                title="Modifier"
                data-testid={`edit-entry-${e.id}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </button>
              <button
                onClick={(ev) => { ev.stopPropagation(); handleDeleteEntry(e.id); }}
                className="opacity-0 group-hover:opacity-100 shrink-0 transition-opacity"
              >
                <Trash2 className="h-2.5 w-2.5" />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const weekLabel = viewMode === "week"
    ? `Semaine du ${dates[0]?.toLocaleDateString("fr-FR", { day: "numeric", month: "long" })} au ${dates[dates.length - 1]?.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}`
    : `${currentDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}`;

  if (loading && entries.length === 0) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const empPool = showInactive ? allEmployees : employees;
  const visibleEmps = empPool.filter(e => isWorkerVisible("employee", e.id));
  const visibleSubs = subcontractors.filter(s => isWorkerVisible("subcontractor", s.id));
  const colCount = 1 + dates.length * 2;

  return (
    <div className="space-y-6" data-testid="work-tracking-tab">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="font-heading font-bold text-sm sm:text-base min-w-[200px] text-center">{weekLabel}</span>
          <Button variant="outline" size="icon" onClick={() => navigate(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>Aujourd'hui</Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={exportEmployeeId} onValueChange={setExportEmployeeId}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="export-employee-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les salariés</SelectItem>
              {employees.map(e => (
                <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf">
            <FileDown className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel} data-testid="export-excel">
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
          </Button>
          <Button variant={viewMode === "week" ? "default" : "outline"} size="sm" onClick={() => setViewMode("week")} data-testid="view-week">Semaine</Button>
          <Button variant={viewMode === "month" ? "default" : "outline"} size="sm" onClick={() => setViewMode("month")} data-testid="view-month">Mois</Button>
          <Button variant="outline" size="sm" onClick={() => setPeopleDialog(true)} data-testid="open-people-dialog">
            <Users className="h-4 w-4 mr-1" /> Personnes
          </Button>
        </div>
      </div>

      <div id="work-tracking-export" className="space-y-6 bg-white">
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-muted/50">
                  <th className="sticky left-0 z-10 bg-muted/50 p-2 text-left min-w-[120px] border-r">Salarié</th>
                  {dates.map(dt => {
                    const isToday = dateStr(dt) === dateStr(new Date());
                    return (
                      <th key={dateStr(dt)} colSpan={2} className={`p-1 text-center border-r min-w-[120px] ${isToday ? "bg-primary/10" : ""}`}>
                        <div className="font-bold">{DAY_NAMES[dt.getDay()]} {dt.getDate()}</div>
                        <div className="font-normal text-muted-foreground text-[9px]">
                          {getDefaultSlots(dt).morning.start}-{getDefaultSlots(dt).afternoon.end}
                        </div>
                      </th>
                    );
                  })}
                </tr>
                <tr className="bg-muted/30">
                  <th className="sticky left-0 z-10 bg-muted/30 border-r"></th>
                  {dates.map(dt => [
                    <th key={`h2m-${dateStr(dt)}`} className="p-1 text-center text-[9px] border-r text-muted-foreground">Matin</th>,
                    <th key={`h2a-${dateStr(dt)}`} className="p-1 text-center text-[9px] border-r text-muted-foreground">Après-midi</th>,
                  ])}
                </tr>
              </thead>
              <tbody>
                {visibleEmps.length > 0 && (
                  <tr className="bg-blue-50/40">
                    <td colSpan={colCount} className="sticky left-0 z-10 bg-blue-50/40 px-2 py-1 text-[10px] font-semibold text-blue-800 uppercase tracking-wide">
                      Salariés ({visibleEmps.length})
                    </td>
                  </tr>
                )}
                {visibleEmps.map(emp => (
                  <tr key={`emp-${emp.id}`} className="border-t hover:bg-zinc-50/50 transition-colors" data-testid={`row-employee-${emp.id}`}>
                    <td className="sticky left-0 z-10 bg-background p-2 border-r font-medium whitespace-nowrap">
                      {emp.first_name} {emp.last_name}
                      {emp.exit_date && <span className="ml-1 text-[9px] text-muted-foreground">(sorti)</span>}
                    </td>
                    {dates.map(dt => [
                      <td key={`${emp.id}-${dateStr(dt)}-m`} className="p-0.5 border-r align-top min-w-[60px]">
                        {renderCell("employee", emp.id, dt, "morning")}
                      </td>,
                      <td key={`${emp.id}-${dateStr(dt)}-a`} className="p-0.5 border-r align-top min-w-[60px]">
                        {renderCell("employee", emp.id, dt, "afternoon")}
                      </td>,
                    ])}
                  </tr>
                ))}
                {visibleSubs.length > 0 && (
                  <tr className="bg-purple-50/40">
                    <td colSpan={colCount} className="sticky left-0 z-10 bg-purple-50/40 px-2 py-1 text-[10px] font-semibold text-purple-800 uppercase tracking-wide">
                      Sous-traitants ({visibleSubs.length})
                    </td>
                  </tr>
                )}
                {visibleSubs.map(sub => {
                  const name = sub.company_name || `${sub.first_name || ""} ${sub.last_name || ""}`.trim() || "Sous-traitant";
                  return (
                    <tr key={`sub-${sub.id}`} className="border-t hover:bg-zinc-50/50 transition-colors" data-testid={`row-subcontractor-${sub.id}`}>
                      <td className="sticky left-0 z-10 bg-background p-2 border-r font-medium whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <span className="text-[9px] px-1 rounded bg-purple-100 text-purple-800 border border-purple-200">ST</span>
                          {name}
                        </span>
                      </td>
                      {dates.map(dt => [
                        <td key={`${sub.id}-${dateStr(dt)}-m`} className="p-0.5 border-r align-top min-w-[60px]">
                          {renderCell("subcontractor", sub.id, dt, "morning")}
                        </td>,
                        <td key={`${sub.id}-${dateStr(dt)}-a`} className="p-0.5 border-r align-top min-w-[60px]">
                          {renderCell("subcontractor", sub.id, dt, "afternoon")}
                        </td>,
                      ])}
                    </tr>
                  );
                })}
                {visibleEmps.length === 0 && visibleSubs.length === 0 && (
                  <tr><td colSpan={colCount} className="text-center text-muted-foreground py-6 text-sm">
                    Toutes les personnes sont masquées. Clique sur « Personnes affichées » pour les afficher.
                  </td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 text-xs">
          {Object.entries(TYPE_LABELS).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1">
              <div className={`w-3 h-3 rounded-sm ${TYPE_COLORS[k]}`}></div>
              <span>{v}</span>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        {comparison && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Comparaison théorique vs réel
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {comparison.orders.length === 0 && comparison.savs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">Aucune donnée de suivi enregistrée</p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Référence</th>
                      <th>Client</th>
                      <th>Jours théoriques</th>
                      <th>Heures réelles</th>
                      <th>Jours réels</th>
                      <th>Écart</th>
                      <th>Indicateur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.orders.map(o => (
                      <tr key={o.order_id}>
                        <td><Badge className="bg-green-100 text-green-800">Commande</Badge></td>
                        <td className="font-mono text-xs">{o.reference}</td>
                        <td>{o.client_name}</td>
                        <td className="font-mono">{o.theoretical_days}j</td>
                        <td className="font-mono">{o.actual_hours}h</td>
                        <td className="font-mono">{o.actual_days}j</td>
                        <td className={`font-mono font-bold ${o.delta_days > 0 ? "text-red-600" : o.delta_days < 0 ? "text-green-600" : ""}`}>
                          {o.delta_days > 0 ? "+" : ""}{o.delta_days}j
                        </td>
                        <td>
                          {o.status === "over" && (
                            <div className="flex items-center gap-1 text-red-600">
                              <TrendingUp className="h-4 w-4" />
                              <span className="text-xs font-bold">+{o.delta_percent}%</span>
                            </div>
                          )}
                          {o.status === "under" && (
                            <div className="flex items-center gap-1 text-green-600">
                              <TrendingDown className="h-4 w-4" />
                              <span className="text-xs font-bold">{o.delta_percent}%</span>
                            </div>
                          )}
                          {o.status === "on_target" && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Minus className="h-4 w-4" />
                              <span className="text-xs">Pile</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {comparison.savs.map(s => (
                      <tr key={s.sav_id}>
                        <td><Badge className="bg-red-100 text-red-800">SAV</Badge></td>
                        <td className="font-mono text-xs">-</td>
                        <td>{s.client_name}</td>
                        <td className="font-mono">{s.theoretical_days}j</td>
                        <td className="font-mono">{s.actual_hours}h</td>
                        <td className="font-mono">{s.actual_days}j</td>
                        <td className={`font-mono font-bold ${s.delta_days > 0 ? "text-red-600" : s.delta_days < 0 ? "text-green-600" : ""}`}>
                          {s.delta_days > 0 ? "+" : ""}{s.delta_days}j
                        </td>
                        <td>
                          {s.status === "over" && <TrendingUp className="h-4 w-4 text-red-600" />}
                          {s.status === "under" && <TrendingDown className="h-4 w-4 text-green-600" />}
                          {s.status === "on_target" && <Minus className="h-4 w-4 text-muted-foreground" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {/* Summary bar */}
              <div className="p-4 bg-muted/30 flex flex-wrap gap-6 text-sm border-t">
                <div><span className="text-muted-foreground">Total suivi :</span> <span className="font-bold">{comparison.summary.total_tracked_hours}h</span></div>
                <div><span className="text-muted-foreground">Commandes :</span> <span className="font-bold text-green-700">{comparison.summary.orders_hours}h</span></div>
                <div><span className="text-muted-foreground">SAV :</span> <span className="font-bold text-red-700">{comparison.summary.sav_hours}h</span></div>
                <div><span className="text-muted-foreground">Autre :</span> <span className="font-bold">{comparison.summary.other_hours}h</span></div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialogs */}
      <AssignWorkDialog
        open={assignDialog}
        onOpenChange={setAssignDialog}
        selection={selection}
        setSelection={setSelection}
        employees={employees}
        subcontractors={subcontractors}
        orders={orders}
        savs={savs}
        dates={dates}
        viewMode={viewMode}
        onAssigned={fetchData}
      />

      <EditWorkEntryDialog
        entry={editEntry}
        onClose={() => setEditEntry(null)}
        onSaved={fetchData}
        employees={employees}
        orders={orders}
        savs={savs}
        suppliers={suppliers}
      />

      <WorkPeopleDialog
        open={peopleDialog}
        onOpenChange={setPeopleDialog}
        showInactive={showInactive}
        toggleShowInactive={toggleShowInactive}
        hiddenWorkerIds={hiddenWorkerIds}
        toggleWorkerVisibility={toggleWorkerVisibility}
        resetVisibility={() => {
          setHiddenWorkerIds({});
          persistPeoplePrefs({}, showInactive);
        }}
        employees={employees}
        allEmployees={allEmployees}
        subcontractors={subcontractors}
      />
    </div>
  );
};

export default WorkTrackingTab;
